import h from 'hyperscript';

import { assert } from '@disy/lib/custom-error';
import { ChangeEvent } from '@disy/lib/event-util';
import { uniqueId } from '@disy/lib/unique-id';
import { FormElement } from '@disy/ui/form-element';

const COMPONENT_NAME = 'd-radio-group';

export interface LabelledValue<T extends string = string> {
  /** The content of the label element, which is inserted after the input element */
  label: string | Node;
  /** The value of the implicitly generated input element within the label element */
  value: T;
  /** If true, the disabled class will be added to the label element, the input element gets disabled */
  disabled?: boolean;
  /** Tooltip to show on hover. */
  title?: string;
  /** Additional description that is displayed next to the label element and does not affect hovering */
  description?: string | Node;
}

export interface RendererValue<T extends string = string> extends LabelledValue<T> {
  inline: boolean;
  name: string;
}

export class RadioGroup<T extends string = string> extends FormElement<T> {
  readonly #renderedValues: HTMLElement[];

  /**
   * @param values - List of values with labels
   * @param [options] - Options
   * @param [options.initialValue] - The initial value, default is the first value
   * @param [options.inline] - Should radio buttons be displayed inline, default is false
   * @param [options.id] - The ID of this element
   * @param [options.name] - Radio group name, default is an auto-generated unique ID
   * @param [options.required] - Whether the radio group is initially required
   * @param [options.renderer] - For custom option rendering; The rendering result must include
   *   an `<input type="radio">` with a `<label>` as the parent element.
   */
  constructor(
    values: LabelledValue<T>[],
    {
      initialValue = values[0].value,
      inline = false,
      id,
      name = uniqueId(),
      required,
      renderer = defaultRenderer,
    }: {
      initialValue?: T;
      inline?: boolean;
      id?: string;
      name?: string;
      required?: boolean;
      renderer?: (item: RendererValue<T>) => HTMLElement;
    } = {},
  ) {
    super({ defaultValue: initialValue, id, name, required });

    assert(values.map((value) => value.value).includes(initialValue), 'Values must include initialValue');

    this.#renderedValues = values.map((value) => {
      const result = renderer({ ...value, inline, name });
      const radio = result.querySelector<HTMLInputElement>('label > input[type=radio]');
      assert(radio, 'Renderer result does not include an <input type="radio"> with a <label> as the parent element');
      radio.dataset.previousDisabled = String(radio.disabled);
      return result;
    });
    this.addEventListener('change', this.#onChange);
  }

  #onChange(event: Event) {
    if (event.target !== this) {
      event.stopImmediatePropagation();
      this.value = (event.target as HTMLInputElement).value as T;
      this.dispatchEvent(new ChangeEvent());
    }
  }

  override connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'radiogroup');
    this.append(...this.#renderedValues);
    super.connectedCallback(); // Call after the radios have been appended.
  }

  override focus() {
    this.querySelector<HTMLInputElement>('input:checked')?.focus();
  }

  protected override applyDisabled(disabled: boolean) {
    this.querySelectorAll('input').forEach((input) => {
      const currentDisabled = input.disabled;
      if (disabled) {
        input.disabled = true;
      } else {
        input.disabled = input.dataset.previousDisabled === 'true';
      }
      input.dataset.previousDisabled = String(currentDisabled);
      const label = input.parentElement!;
      label.classList.toggle('is-disabled', input.disabled);
    });
  }

  protected override applyValue(value: T) {
    this.querySelectorAll('input').forEach((input) => {
      input.checked = input.value === value;
    });
  }
}

customElements.define(COMPONENT_NAME, RadioGroup);

function defaultRenderer({ inline, name, value, label, title, disabled = false, description }: RendererValue) {
  const labelElement = h(
    'label.d-form-check',
    title ?
      {
        title,
      }
    : {},
    [h('input', { type: 'radio', name, value, disabled }), label],
  );
  labelElement.classList.toggle('d-form-check-inline', inline);
  labelElement.classList.toggle('is-disabled', disabled);
  return description ? h('.d-stack-h.space-2', [labelElement, description]) : labelElement;
}
